import { useState, useRef } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  getReferencesPendingImageRetry,
  attachReferenceImageFromUrl,
} from '../../services/firebase';
import type { WatchBrand, WatchReference } from '../../types';
import toast from 'react-hot-toast';

interface RetryMissingImagesProps {
  brand: WatchBrand;
  /** Called after a run completes so the parent can refresh its reference list. */
  onComplete?: () => void;
}

type RowStatus = 'pending' | 'processing' | 'recovered' | 'failed';

interface RetryRow {
  ref: WatchReference;
  status: RowStatus;
  error?: string;
}

/**
 * Admin recovery action: finds references whose image fetch failed during bulk
 * import (empty `images` + a persisted `pendingImageUrl`) and re-runs the
 * fetch → upload flow against that saved URL. Purely client-side — reuses the
 * same {@link attachReferenceImageFromUrl} helper as the importer, so the
 * 3-strategy fetch chain is identical.
 */
export function RetryMissingImages({ brand, onComplete }: RetryMissingImagesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [rows, setRows] = useState<RetryRow[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const abortRef = useRef(false);

  const recoveredCount = rows.filter((r) => r.status === 'recovered').length;
  const failedCount = rows.filter((r) => r.status === 'failed').length;

  const scan = async () => {
    setScanning(true);
    setRows([]);
    setDone(false);
    setProgress(0);
    try {
      const candidates = await getReferencesPendingImageRetry(brand.id);
      setRows(candidates.map((ref) => ({ ref, status: 'pending' as const })));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to scan for retry candidates');
    } finally {
      setScanning(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    void scan();
  };

  const handleClose = () => {
    if (running) return;
    setIsOpen(false);
    setRows([]);
    setDone(false);
    setProgress(0);
    abortRef.current = false;
  };

  const updateRow = (index: number, update: Partial<RetryRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...update } : r)));
  };

  const run = async () => {
    setRunning(true);
    setDone(false);
    setProgress(0);
    abortRef.current = false;

    // Process a few at a time so a slow fetch on one doesn't serialize the whole
    // queue, mirroring the bulk importer's bounded-parallel batching.
    const BATCH_SIZE = 4;
    const current = rows;
    for (let start = 0; start < current.length; start += BATCH_SIZE) {
      if (abortRef.current) break;
      const batch = current.slice(start, start + BATCH_SIZE);
      await Promise.all(
        batch.map(async (row, offset) => {
          const i = start + offset;
          if (abortRef.current) return;
          const sourceUrl = row.ref.pendingImageUrl;
          if (!sourceUrl) {
            updateRow(i, { status: 'failed', error: 'No pending URL' });
            return;
          }
          updateRow(i, { status: 'processing' });
          try {
            await attachReferenceImageFromUrl(brand.id, row.ref.id, sourceUrl);
            // On success the helper clears pendingImageUrl, so the ref drops out
            // of the retry queue on the next scan.
            updateRow(i, { status: 'recovered' });
          } catch (err) {
            // Leave pendingImageUrl in place so it stays a retry candidate.
            updateRow(i, {
              status: 'failed',
              error: err instanceof Error ? err.message : 'Image fetch failed',
            });
          } finally {
            setProgress((p) => p + 1);
          }
        }),
      );
    }

    setRunning(false);
    setDone(true);
    onComplete?.();
  };

  const total = rows.length;

  return (
    <>
      <Button variant="secondary" onClick={handleOpen}>
        Retry Missing Images
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} title="Retry Missing Images" size="lg">
        <div className="space-y-4">
          {scanning && (
            <p className="text-sm text-gray-600">Scanning for references with failed images…</p>
          )}

          {!scanning && total === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                No retry candidates found — every reference either has an image or no saved source URL to retry.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => void scan()}>Re-scan</Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          )}

          {!scanning && total > 0 && (
            <>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{total}</span> retry candidate{total === 1 ? '' : 's'} found
                {' '}— reference{total === 1 ? '' : 's'} with no image but a saved source URL.
                {(running || done) && (
                  <span className="ml-2 text-gray-500">
                    {progress}/{total} processed
                    {(recoveredCount > 0 || failedCount > 0) && (
                      <> · <span className="text-green-600">{recoveredCount} recovered</span> · <span className="text-red-600">{failedCount} failed</span></>
                    )}
                  </span>
                )}
              </p>

              {(running || done) && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${total ? (progress / total) * 100 : 0}%` }}
                  />
                </div>
              )}

              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {rows.map((row) => (
                  <div key={row.ref.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-mono text-gray-800 truncate">{row.ref.reference}</div>
                      <div className="text-gray-500 truncate" title={row.ref.pendingImageUrl}>
                        {row.ref.model || row.ref.productName || '—'}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {row.status === 'pending' && <span className="text-gray-400 text-xs">Queued</span>}
                      {row.status === 'processing' && <span className="text-indigo-600 text-xs">Fetching…</span>}
                      {row.status === 'recovered' && <span className="text-green-600 text-xs font-medium">✓ Recovered</span>}
                      {row.status === 'failed' && (
                        <span className="text-red-600 text-xs" title={row.error}>✗ Failed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                {!running && !done && (
                  <>
                    <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                    <Button onClick={() => void run()}>Retry {total} Image{total === 1 ? '' : 's'}</Button>
                  </>
                )}
                {running && (
                  <Button variant="secondary" onClick={() => { abortRef.current = true; }}>
                    Stop
                  </Button>
                )}
                {done && (
                  <>
                    <Button variant="secondary" onClick={() => void scan()}>Re-scan</Button>
                    <Button onClick={handleClose}>Done</Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
